import { isNil } from "lodash-es";

import { SYSTEM_GENERATED_COLORS, THEME_MODEL } from "./model/index.js";
import { cloneInstanceObject, getColorVariables } from "../utils/index.js";
import { resolveResourceURL } from "./utils.js";
import { useAccountStore } from "../stores/account.js";
import {
  KEY_PRIMARY_500,
  DEFAULT_COLOR_CODE
} from "@base/common/constants/index.js";

export class ThemeModel {
  constructor(values, applyBackground) {
    this.values = values || cloneInstanceObject(THEME_MODEL);
    this.changeHandlers = [];
    this.applyBackground = applyBackground;
    this.isContrast = false;

    if (!values) {
      Object.entries(this.values).forEach(([key, value]) => {
        if (value.ref && this.values[key]) {
          this.values[key] = {
            ...this.values[key],
            defaultRef: value.ref
          };
        }
      });
    }
  }

  load = (values) => {
    try {
      let { AccountTheme } = useAccountStore.getState();
      if (AccountTheme) {
        this.values[KEY_PRIMARY_500].defaultValue =
          AccountTheme?.Colors?.[KEY_PRIMARY_500]?.Value || DEFAULT_COLOR_CODE;
      }
      Object.entries(values).forEach(([key, obj]) => {
        if (!isNil(obj.value) && !obj.isNew) {
          this.setValue(key, obj.value, false);
        } else if (obj.ref) {
          this.setRef(key, obj.ref, false);
        } else if (obj.isNew) {
          // used for inserting custom colors
          this.values[key] = { ...obj };
        }
      });
    } catch (err) {
      console.error(err.message);
    }
  };

  getValues = () => cloneInstanceObject(this.values); // Immer is Object.freeze() ing this object :(
  getApplyBackground = () => this.applyBackground;

  setApplyBackground = (color, applyBg) => {
    this.applyBackground = applyBg;
    const { grayPallet, bgColor, isContrast, primaryColor } = getColorVariables(
      color,
      this.applyBackground
    );
    const colorPalettes = Object.assign(bgColor, primaryColor);
    this.setContrast(isContrast);
    Object.entries(grayPallet).forEach(([key, value]) => {
      this.setNeutralValue(key, value);
    });
    Object.entries(colorPalettes).forEach(([key, value]) => {
      this.setValue(key, value);
    });
  };

  setContrast = (value) => {
    this.isContrast = value;
  };

  getContrast = () => {
    return this.isContrast;
  };

  setValue = (key, value, shouldTrigger = true) => {
    if (!this.values[key] || !value) return;
    this.values[key].value = value;
    if (this.values[key].ref) {
      Reflect.deleteProperty(this.values[key], "ref");
    }
    this.values[key].modified =
      this.values[key].value !== this.values[key].defaultValue;
    shouldTrigger && this.trigger(key);
  };

  setNeutralValue = (key, value, shouldTrigger = true) => {
    this.values[key].value = value;
    this.values[key].defaultValue = value;
    this.values[key].modified = false;
    shouldTrigger && this.trigger(key);
  };

  setRef = (key, value, shouldTrigger = true) => {
    if (!this.values[key]) return;
    this.values[key].ref = value;
    if (this.values[key].value) {
      Reflect.deleteProperty(this.values[key], "value");
    }
    this.values[key].modified = true;
    shouldTrigger && this.trigger(key);
  };

  reset = (key, isGroup) => {
    const resetValue = (key) => {
      if (!this.values[key]) {
        console.error("Item not found, ", this.values[key]);
        return;
      }

      if ("ref" in this.values[key] || "defaultRef" in this.values[key]) {
        // This value doesn't have a defaultValue, the defaultValue is a reference to someother value.
        this.values[key].ref = this.values[key].defaultRef;
        Reflect.deleteProperty(this.values[key], "value");
      } else {
        this.values[key].value = this.values[key].defaultValue;
      }
      Reflect.deleteProperty(this.values[key], "modified");
    };

    if (isGroup) {
      Object.keys(this.values)
        .filter((k) => k.indexOf(key) === 0)
        .forEach((key) => {
          resetValue(key);
        });
    } else {
      resetValue(key);
    }
    this.trigger(key);
  };

  filterkeys = (key) => {
    return SYSTEM_GENERATED_COLORS.some((color) => key.includes(color));
  };

  toJSON = () => {
    const data = Object.entries(this.values).reduce((memo, [key, obj]) => {
      if (obj.modified || this.filterkeys(key)) {
        if (!isNil(obj.value)) {
          memo[key] = { value: obj.value };
        } else if (obj.ref) {
          memo[key] = { ref: obj.ref };
        }
      }
      return memo;
    }, {});
    return data;
  };

  keyToCSS(key) {
    return `--kf-${key.toLowerCase().split(".").join("-")}`;
  }

  graykeyToCSS(key) {
    return `--${key.toLowerCase().split(".").join("-")}`;
  }

  getValue = (key, autoPriority) => {
    const obj = this.values[key];
    if (!obj) return;
    if (!isNil(obj.value)) {
      return obj.value;
    }
    if (!autoPriority && obj.ref) {
      return this.getValue(obj.ref);
    }
    if (obj.valueFunc) {
      return obj.valueFunc(this.values);
    }
    if (autoPriority && obj.ref) {
      return this.getValue(obj.ref);
    }
    return obj.defaultValue;
  };

  resolveValue = (key) => {
    const obj = this.values[key];

    if (!obj) return;
    if (obj.value) {
      if (obj.value.startsWith("resource")) {
        return resolveResourceURL(obj.value);
      }
      return obj.value;
    }
    if (obj.ref) {
      return `var(${this.keyToCSS(obj.ref)})`;
    }
    if (obj.valueFunc) {
      return obj.valueFunc(this.values);
    }
    return obj.defaultValue;
  };

  toCSS = () => {
    let css = "";
    Object.entries(this.values).forEach(([key, obj]) => {
      if (obj.modified || this.filterkeys(key)) {
        try {
          if (!key.includes("Color")) {
            css += `${this.graykeyToCSS(key)}: ${this.resolveValue(key)};\r\n`;
          } else {
            css += `${this.keyToCSS(key)}: ${this.resolveValue(key)};\r\n`;
            if (obj.referredIn) {
              obj.referredIn.forEach((key) => {
                css += `${this.keyToCSS(key)}: ${this.resolveValue(key)};\r\n`;
              });
            }
          }
        } catch (ex) {
          // console.log("EX", ex);
        }
      }
    });
    return css;
  };

  toCSSObject = () => {
    let css = [];
    Object.entries(this.values).forEach(([key, obj]) => {
      if (obj.modified || this.filterkeys(key)) {
        try {
          css[key] = this.resolveValue(key);
          if (obj.referredIn) {
            obj.referredIn.forEach((key) => {
              css[key] = this.resolveValue(key);
            });
          }
        } catch (ex) {
          // console.log("EX", ex);
          throw ex;
        }
      }
    });
    return css;
  };

  toAllValues = () => {
    const json = {};
    Object.keys(this.values).forEach((key) => {
      json[key] = this.getValue(key);
    });
    return json;
  };

  onChange = (callback) => {
    this.changeHandlers.push(callback);
  };

  trigger(data) {
    this.changeHandlers.forEach((callback) => {
      callback(data);
    });
  }
}

export function Theme(values, applyBackground) {
  return new ThemeModel(values, applyBackground);
}
window.Theme = Theme;

export * from "./utils.js";
export * from "./constants/index.js";
export * from "./model/index.js";
export * from "./context.js";
